import React, {useRef, useState} from 'react';
import {useRouter} from 'next/router';
import {ChevronRightIcon} from '@heroicons/react-v2/24/outline';

import Link from '../Link';
import {Badge} from '../Badge';
import CollapserThing from '../CollapserThing';
import RailTooltip from './RailTooltip';
import {useSidebar} from './SidebarContext';
import {
  GUTTER_CLASS,
  LABEL_INSET_CLASS,
  NEUTRAL_PLATE_CLASS,
  PRESS_PLATE_CLASS,
  SLOT_CLASS,
  labelTransition,
} from './geometry';
import SidebarMenu from '../../constants/sidebarMenu';
import {useAppContext, useAnalytics} from '../../hooks';
import {cx} from '../../util';

const slugify = (text) => String(text).toLowerCase().replace(/[^a-z0-9]+/g, '-');

/** Resolve a menu `link`, which may be a function of appContext. */
export const resolveLink = (link, appContext) => (
  typeof link === 'function' ? link(appContext) : link
);

const rowClasses = (isActive) => cx(
  `group relative flex h-10 w-full items-center rounded-lg text-sm font-medium
   outline-none transition-colors duration-150
   focus-visible:ring-2 focus-visible:ring-indigo-400`,
  isActive ? 'text-indigo' : 'text-gray-600 hover:text-gray-900',
);

/**
 * The row's background, as a separate layer. Keeping it off the row itself is
 * what lets the press effect shrink the plate while the icon and label hold
 * their positions — see PRESS_PLATE_CLASS.
 */
const RowPlate = ({isActive}) => (
  <span
    aria-hidden="true"
    className={cx(
      PRESS_PLATE_CLASS,
      // The active row already carries a fill, so the inset alone is enough to
      // register the press; darkening it too reads as a state change.
      isActive ? 'bg-indigo-100' : cx('bg-transparent', NEUTRAL_PLATE_CLASS),
    )}
  />
);

const ItemIcon = ({icon: Icon, isActive}) => (
  <span className={cx(SLOT_CLASS, 'relative')}>
    {Icon && (
      <Icon
        className={cx(
          'h-5 w-5 transition-colors duration-150',
          isActive ? 'text-indigo' : 'text-gray-400 group-hover:text-gray-600',
        )}
        aria-hidden="true"
      />
    )}
  </span>
);

/**
 * A single row in the sidebar.
 *
 * The icon sits in a fixed slot and the label is absolutely positioned, so
 * neither moves when the sidebar changes width — see ./geometry. Collapsing
 * animates the panel width and fades the label; nothing here reflows.
 */
const SidebarItem = ({item, isActive, onNavigate}) => {
  const appContext = useAppContext();
  const {
    showLabels,
    flyout,
    hoveredItem,
    requestFlyout,
    closeFlyout,
    requestTooltip,
    clearTooltip,
  } = useSidebar();
  const rowRef = useRef(null);

  const hasChildren = Boolean(item.items?.length);
  const triggerId = `sidebar-item-${slugify(item.title)}`;
  const isFlyoutOpen = hasChildren && flyout?.key === item.title;
  const link = resolveLink(item.link, appContext);

  // Only one of the two hover affordances shows at a time: the tooltip names an
  // icon in the rail, and is redundant once labels or a flyout are visible.
  const showTooltip = !showLabels && hoveredItem === item.title && !isFlyoutOpen;

  const descriptor = hasChildren ? {
    key: item.title,
    title: item.flyoutTitle,
    items: item.items,
    cta: item.cta,
    onNavigate,
    width: item.flyoutWidth,
    closeDelay: item.flyoutCloseDelay,
    labelledBy: triggerId,
  } : null;

  // Rows without a submenu request `null`, which closes whatever is open —
  // subject to the same aim test, so brushing past one on the way to a panel
  // does not dismiss it.
  const handleMouseEnter = () => {
    requestTooltip(item.title);
    requestFlyout(descriptor);
  };

  const handleMouseLeave = () => clearTooltip(item.title);

  // mt-px: the label's cap height sits optically a pixel high against the
  // icons at this size, so it gets a nudge down.
  const labelClasses = cx(
    'absolute mt-px flex items-center gap-2 whitespace-nowrap',
    LABEL_INSET_CLASS,
    hasChildren ? 'right-8' : 'right-3',
    labelTransition(showLabels),
  );

  const rowContents = (
    <>
      <RowPlate isActive={isActive} />
      <ItemIcon icon={item.icon} isActive={isActive} />
      <span className={labelClasses}>
        <span className="truncate">{item.title}</span>
        {item.badge && <Badge className="shrink-0 px-1.5 py-0">{item.badge}</Badge>}
      </span>
      {hasChildren && (
        <ChevronRightIcon
          className={cx(
            'absolute right-2.5 h-4 w-4 text-gray-300 transition-[opacity,transform] duration-150',
            labelTransition(showLabels),
            isFlyoutOpen && 'translate-x-0.5 text-gray-400',
          )}
          aria-hidden="true"
        />
      )}
    </>
  );

  const describedBy = showTooltip ? `${triggerId}-tooltip` : undefined;

  return (
    <li className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div ref={rowRef} className="flex items-center">
        {link ? (
          <Link
            id={triggerId}
            href={link}
            forceAnchor={item.forceAnchor}
            onClick={() => onNavigate?.(item)}
            aria-current={isActive ? 'page' : undefined}
            aria-describedby={describedBy}
            className={rowClasses(isActive)}
          >
            {rowContents}
          </Link>
        ) : (
          <button
            id={triggerId}
            type="button"
            aria-haspopup="true"
            aria-expanded={isFlyoutOpen}
            aria-describedby={describedBy}
            onClick={() => (isFlyoutOpen
              ? closeFlyout()
              : requestFlyout(descriptor, {immediate: true}))}
            className={rowClasses(isActive)}
          >
            {rowContents}
          </button>
        )}
      </div>

      <RailTooltip open={showTooltip} anchorRef={rowRef} id={`${triggerId}-tooltip`}>
        {item.title}
      </RailTooltip>
    </li>
  );
};

/**
 * The drawer variant of a nav row. A horizontal flyout is the wrong shape on a
 * phone, so children expand inline underneath the parent instead.
 */
const InlineSidebarItem = ({item, isActive, onNavigate}) => {
  const appContext = useAppContext();
  const [expanded, setExpanded] = useState(false);
  const hasChildren = Boolean(item.items?.length);
  const link = resolveLink(item.link, appContext);
  const listId = `sidebar-drawer-${slugify(item.title)}`;

  const label = (
    <>
      <RowPlate isActive={isActive} />
      <ItemIcon icon={item.icon} isActive={isActive} />
      <span className="relative flex min-w-0 flex-1 items-center gap-2 pr-2">
        <span className="truncate">{item.title}</span>
        {item.badge && <Badge className="shrink-0 px-1.5 py-0">{item.badge}</Badge>}
      </span>
    </>
  );

  return (
    <li>
      {/* The disclosure is a sibling rather than an overlay, so tapping the row
          navigates and tapping the chevron expands — both with a full-size
          target. */}
      <div className="flex items-center gap-0.5">
        {link ? (
          <Link
            href={link}
            forceAnchor={item.forceAnchor}
            onClick={() => onNavigate?.(item)}
            aria-current={isActive ? 'page' : undefined}
            className={cx(rowClasses(isActive), hasChildren && 'flex-1')}
          >
            {label}
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-controls={listId}
            className={cx(rowClasses(isActive), hasChildren && 'flex-1')}
          >
            {label}
          </button>
        )}

        {hasChildren && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-controls={listId}
            aria-label={`${item.title}: ${expanded ? 'hide' : 'show'} submenu`}
            className="flex h-10 w-9 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100/80"
          >
            <ChevronRightIcon
              className={cx('h-4 w-4 transition-transform duration-200', expanded && 'rotate-90')}
              aria-hidden="true"
            />
          </button>
        )}
      </div>

      {hasChildren && (
        <CollapserThing visible={expanded}>
          <ul id={listId} className="mt-0.5 ml-6 flex flex-col gap-0.5 border-l border-gray-200 pl-2">
            {item.items.map((child) => (
              <li key={child.title}>
                <Link
                  href={child.link}
                  forceAnchor={child.forceAnchor}
                  onClick={() => onNavigate?.(child)}
                  className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100/80 hover:text-gray-900"
                >
                  {child.icon && <child.icon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />}
                  <span className="truncate">{child.title}</span>
                  {child.badge && <Badge className="shrink-0 px-1.5 py-0">{child.badge}</Badge>}
                </Link>
              </li>
            ))}
            {item.cta && (
              <li>
                <Link
                  href={item.cta.link}
                  onClick={() => onNavigate?.(item.cta)}
                  className="block rounded-lg px-2.5 py-2 text-sm font-medium text-indigo"
                >
                  {item.cta.title}
                </Link>
              </li>
            )}
          </ul>
        </CollapserThing>
      )}
    </li>
  );
};

/**
 * Renders SidebarMenu.sections, filtered for the current user.
 *
 * @param {boolean} [props.inline] Use the drawer variant, where nested items
 *   expand downward instead of revealing a horizontal flyout.
 * @param {function} [props.onNavigate] Called with the item that was clicked.
 */
const SidebarNav = ({inline = false, onNavigate, className}) => {
  const appContext = useAppContext();
  const {tracker} = useAnalytics();
  const {showLabels} = useSidebar();
  const {pathname} = useRouter();

  const isVisible = (entry) => !entry.shouldHide?.(appContext);

  const sections = SidebarMenu.sections
    .filter(isVisible)
    .map((section) => ({...section, items: section.items.filter(isVisible)}))
    .filter((section) => section.items.length > 0);

  // Longest matching prefix wins
  const matchLength = (item) => Math.max(
    item.activePaths?.includes(pathname) ? pathname.length : -1,
    -1,
    ...(item.activePrefixes ?? [])
      .filter((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
      .map((prefix) => prefix.length),
  );

  const activeItem = sections
    .flatMap((section) => section.items)
    .reduce((best, item) => {
      const length = matchLength(item);
      return length > best.length ? {item, length} : best;
    }, {item: null, length: -1})
    .item;

  const isActive = (item) => item === activeItem;

  const handleNavigate = (item) => {
    tracker.headerClickMenu({
      platform: inline ? 'mobile-sidebar' : 'sidebar',
      title: item.title,
      link: resolveLink(item.link, appContext),
    });
    onNavigate?.(item);
  };

  const Item = inline ? InlineSidebarItem : SidebarItem;

  return (
    <nav
      aria-label="Main navigation"
      className={cx('flex flex-col gap-4', inline ? 'px-1' : GUTTER_CLASS, className)}
    >
      {sections.map((section, index) => (
        <div key={section.title || `section-${index}`}>
          {section.title && (
            // Fixed height in both states: if this collapsed away with its
            // text, every item below it would shift as the sidebar animates.
            <div className="relative h-6">
              <span
                className={cx(
                  'absolute top-1 whitespace-nowrap text-[11px] font-semibold tracking-wider text-gray-400 uppercase',
                  inline ? 'left-2.5' : LABEL_INSET_CLASS,
                  labelTransition(inline || showLabels),
                )}
              >
                {section.title}
              </span>
            </div>
          )}
          <ul className="flex flex-col gap-0.5">
            {section.items.map((item) => (
              <Item
                key={item.title}
                item={item}
                isActive={isActive(item)}
                onNavigate={handleNavigate}
              />
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
};

export default SidebarNav;
